import PageContent from '@/layout/PageContent'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import LoadingSpinner from '@/components/ui/loading'
import { useQueryDeliverableContinuationContext } from '@/services/campaigns/mobile-consumer-queries'
import { useMemo } from 'react'

export default function MobileConsumerDeliverableLandingPage() {
    const { deliverableId } = useParams()
    const navigate = useNavigate()

    const { data, isLoading, isError, refetch, isFetching } = useQueryDeliverableContinuationContext({
        pathParams: { deliverableId: deliverableId || '' },
        enabled: !!deliverableId,
    })

    const payload = (data as any)?.data !== undefined ? (data as any).data : data
    const context = payload?.context

    const processedEntries = useMemo(() => Object.entries(context?.processedVariables || {}), [context?.processedVariables])
    const communicationEntries = useMemo(
        () => Object.entries(context?.communicationVariables || {}),
        [context?.communicationVariables]
    )
    const qualityEntries = useMemo(() => Object.entries(context?.qualitySnapshot || {}), [context?.qualitySnapshot])

    const missingProof = useMemo(() => {
        const likely = qualityEntries.filter(([k]) => /missing|evidence|proof|gap/i.test(k))
        return likely.slice(0, 6)
    }, [qualityEntries])

    const cleanedHighlights = useMemo(() => processedEntries.slice(0, 6), [processedEntries])
    const usableHighlights = useMemo(() => communicationEntries.slice(0, 6), [communicationEntries])

    if (!deliverableId) {
        return (
            <PageContent>
                <div className='max-w-3xl mx-auto py-10'>
                    <p className='text-sm text-gray-600'>Missing deliverable id.</p>
                </div>
            </PageContent>
        )
    }

    if (isLoading) return <LoadingSpinner />

    if (isError || !context) {
        return (
            <PageContent>
                <div className='max-w-3xl mx-auto py-10 space-y-3'>
                    <h1 className='text-xl font-semibold text-gray-900'>Deliverable unavailable</h1>
                    <p className='text-sm text-gray-600'>
                        We couldn’t load this deliverable in your web workspace. Try again from your email link, or go
                        back home.
                    </p>
                    <Button variant='brand' onClick={() => navigate('/dashboard/home')}>
                        Back to Home
                    </Button>
                </div>
            </PageContent>
        )
    }

    const title = context.deliverableTitle || 'Deliverable'
    const cdnUrl = context.deliverableCdnUrl as string | null
    const srcDoc = context.deliverableContent as string | null
    const livingBrief = context.livingBrief || {}
    const livingBriefDocId = livingBrief?.docId as string | null
    const livingBriefError = livingBrief?.error as string | null

    return (
        <PageContent>
            <div className='max-w-5xl mx-auto py-8 space-y-6'>
                <div className='rounded-2xl border bg-white p-6 space-y-4'>
                    <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3'>
                        <div className='space-y-1'>
                            <h1 className='text-2xl font-semibold text-gray-900'>Your {title} is ready</h1>
                            {context.deliverableDescription ? (
                                <p className='text-sm text-gray-600'>{context.deliverableDescription}</p>
                            ) : (
                                <p className='text-sm text-gray-600'>Your interview has been turned into a living workspace asset.</p>
                            )}
                        </div>

                        <div className='flex flex-wrap gap-2'>
                            <Button
                                variant='brand'
                                onClick={() => {
                                    navigate(`/dashboard/campaigns/utilities${livingBriefDocId ? `?sourceDocId=${livingBriefDocId}` : ''}`)
                                }}
                            >
                                Build from this brief
                            </Button>

                            <Button
                                variant='outline'
                                onClick={() => {
                                    navigate(`/dashboard/campaigns/utilities/studio?deliverableId=${deliverableId}`)
                                }}
                            >
                                Build in Studio
                            </Button>

                            <Button
                                variant='outline'
                                disabled={!livingBriefDocId}
                                onClick={() => livingBriefDocId && navigate(`/dashboard/documents/file/${livingBriefDocId}`)}
                            >
                                Open Living Brief
                            </Button>

                            <Button
                                variant='outline'
                                onClick={() => {
                                    const continuation = context
                                    sessionStorage.setItem('mobileConsumerContinuationContext', JSON.stringify(continuation))
                                    navigate('/dashboard/agent', { state: { continuation } })
                                }}
                            >
                                Sharpen with Agent
                            </Button>

                            <Button
                                variant='outline'
                                disabled={!cdnUrl && !srcDoc}
                                onClick={() => {
                                    if (cdnUrl) window.open(cdnUrl, '_blank', 'noopener,noreferrer')
                                    else if (srcDoc) {
                                        const w = window.open('', '_blank', 'noopener,noreferrer')
                                        if (w) w.document.write(srcDoc)
                                    }
                                }}
                            >
                                View original deliverable
                            </Button>

                            <Button variant='ghost' onClick={() => navigate('/dashboard/home')}>
                                Continue
                            </Button>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                        <div className='rounded-xl border bg-slate-50 p-4'>
                            <div className='text-xs font-semibold uppercase tracking-wide text-slate-600'>Saved as a Living Brief</div>
                            <div className='mt-2 text-sm text-slate-900 font-medium'>
                                {livingBriefDocId ? 'Saved to your workspace' : 'Saving…'}
                            </div>
                            <div className='mt-1 text-xs text-slate-600'>
                                {livingBriefDocId ? `Document: ${livingBriefDocId}` : 'We’re saving this automatically.'}
                            </div>
                            {livingBriefError ? (
                                <div className='mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
                                    <div className='font-semibold'>Couldn’t save automatically</div>
                                    <div className='mt-1'>{livingBriefError}</div>
                                    <div className='mt-2'>
                                        <Button variant='outline' disabled={isFetching} onClick={() => refetch()}>
                                            {isFetching ? 'Retrying…' : 'Retry save'}
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className='rounded-xl border bg-slate-50 p-4'>
                            <div className='text-xs font-semibold uppercase tracking-wide text-slate-600'>Cleaned from your interview</div>
                            <div className='mt-2 text-sm text-slate-900 font-medium'>
                                {processedEntries.length} cleaned fields
                            </div>
                            <div className='text-xs text-slate-600 mt-1'>{communicationEntries.length} usable outputs</div>
                            <div className='text-xs text-slate-600 mt-1'>{Object.keys(context.qualitySnapshot || {}).length} quality signals</div>
                        </div>

                        <div className='rounded-xl border bg-slate-50 p-4'>
                            <div className='text-xs font-semibold uppercase tracking-wide text-slate-600'>Next move</div>
                            <div className='mt-2 text-sm text-slate-900 font-medium'>Turn this into something deployable</div>
                            <div className='text-xs text-slate-600 mt-1'>Generate a bio pack, PR hooks, a quote bank, or a pitch pack.</div>
                        </div>
                    </div>

                    <div className='rounded-2xl border bg-gradient-to-r from-slate-50 to-white p-5'>
                        <div className='text-sm font-semibold text-slate-900'>Before → After</div>
                        <div className='text-sm text-slate-600 mt-1'>
                            CommZone turns a messy conversation into clean structure and deployable copy.
                        </div>
                        <div className='mt-4 grid grid-cols-1 md:grid-cols-2 gap-3'>
                            <div className='rounded-xl border bg-white p-4'>
                                <div className='text-xs font-semibold uppercase tracking-wide text-slate-600'>Cleaned information</div>
                                <div className='mt-2 space-y-2'>
                                    {cleanedHighlights.slice(0, 3).map(([k, v]: any) => (
                                        <div key={k} className='rounded-lg border bg-slate-50 p-3'>
                                            <div className='text-xs font-semibold text-slate-700'>{k}</div>
                                            <div className='text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words'>
                                                {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='rounded-xl border bg-white p-4'>
                                <div className='text-xs font-semibold uppercase tracking-wide text-slate-600'>Ready-to-use copy</div>
                                <div className='mt-2 space-y-2'>
                                    {usableHighlights.slice(0, 3).map(([k, v]: any) => (
                                        <div key={k} className='rounded-lg border bg-slate-50 p-3'>
                                            <div className='text-xs font-semibold text-slate-700'>{k}</div>
                                            <div className='text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words'>
                                                {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                    <div className='rounded-2xl border bg-white p-5 space-y-3'>
                        <div className='text-sm font-semibold text-gray-900'>What we cleaned</div>
                        <div className='space-y-2'>
                            {cleanedHighlights.length ? (
                                cleanedHighlights.map(([k, v]: any) => (
                                    <div key={k} className='rounded-lg border bg-slate-50 p-3'>
                                        <div className='text-xs font-semibold text-slate-700'>{k}</div>
                                        <div className='text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words'>
                                            {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className='text-sm text-slate-600'>No cleaned fields available.</div>
                            )}
                        </div>
                    </div>

                    <div className='rounded-2xl border bg-white p-5 space-y-3'>
                        <div className='text-sm font-semibold text-gray-900'>What you can use now</div>
                        <div className='space-y-2'>
                            {usableHighlights.length ? (
                                usableHighlights.map(([k, v]: any) => (
                                    <div key={k} className='rounded-lg border bg-slate-50 p-3'>
                                        <div className='text-xs font-semibold text-slate-700'>{k}</div>
                                        <div className='text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words'>
                                            {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className='text-sm text-slate-600'>No ready-to-use outputs available yet.</div>
                            )}
                        </div>
                    </div>

                    <div className='rounded-2xl border bg-white p-5 space-y-3'>
                        <div className='text-sm font-semibold text-gray-900'>Missing proof</div>
                        <div className='space-y-2'>
                            {missingProof.length ? (
                                missingProof.map(([k, v]: any) => (
                                    <div key={k} className='rounded-lg border bg-amber-50 p-3'>
                                        <div className='text-xs font-semibold text-amber-900'>{k}</div>
                                        <div className='text-xs text-amber-900/80 mt-1 whitespace-pre-wrap break-words'>
                                            {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className='text-sm text-slate-600'>No missing-proof flags detected.</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='rounded-xl border bg-white overflow-hidden'>
                    {cdnUrl ? (
                        <iframe title='Deliverable preview' src={cdnUrl} className='w-full h-[70vh]' />
                    ) : srcDoc ? (
                        <iframe title='Deliverable preview' srcDoc={srcDoc} className='w-full h-[70vh]' />
                    ) : (
                        <div className='p-6 text-sm text-gray-600'>
                            Preview unavailable for this deliverable type.
                        </div>
                    )}
                </div>
            </div>
        </PageContent>
    )
}

